import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "../../../configuration";
import { CommentSubject } from "../../../constants";
import { CreateCommentDto, CreateCommentResultDto, FetchError, UserDto } from "../../types";
import { useAccessToken } from "../../use-access-token";
import { queryKeys } from "../queryKeys";

type Variables = {
  subjectType: CommentSubject;
  subjectGuid: string;
  commentGuid: string;
  createdBy: UserDto;
  text: string;
};

const useCreateCommentRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function createCommentRequest(variables: Variables): Promise<CreateCommentResultDto> {
    const accessToken = await getAccessToken();
    const body: CreateCommentDto = {
      subjectType: variables.subjectType,
      commentGuid: variables.commentGuid,
      createdByUserId: variables.createdBy.userId,
      text: variables.text,
    };

    const response = await fetch(`${configuration.apiRootUrl}/threads/${variables.subjectGuid}/comments`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "POST",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }

    return response.json();
  }

  return { createCommentRequest };
};

const useCreateComment = () => {
  const { createCommentRequest } = useCreateCommentRequest();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createCommentRequest,
    onMutate: async (_variables) => {},
    onError: (_error, _variables, _context) => {},
    onSuccess: (_data, variables) => {
      switch (variables.subjectType) {
        case CommentSubject.ActionItem:
          queryClient.invalidateQueries(queryKeys.actionItems.item(variables.subjectGuid));
          break;
        case CommentSubject.Issue:
          queryClient.invalidateQueries(queryKeys.issues.getIssueFeed(variables.subjectGuid));
          break;
        case CommentSubject.TaskItem:
          queryClient.invalidateQueries(queryKeys.taskItems.getTaskItemFeed(variables.subjectGuid));
          break;
        case CommentSubject.Team:
          queryClient.invalidateQueries(queryKeys.teams.getTeamFeed(variables.subjectGuid));
          break;
      }
    },
    onSettled: (_data, _error, _variables) => {},
  });
};

export { useCreateComment };
